import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star, Phone, MapPin, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedInventory from '../../../../components/RelatedInventory';

const AlfaRomeoService = () => {
  const alfaFeatures = [
    'MES & SDD Diagnostics',
    'Italian Engineering Experts',
    'Genuine Alfa Parts',
    'Electrical Specialists',
    'Performance Tuning',
    'Classic & Modern Models'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      title: 'Electrical Diagnostics',
      icon: Zap,
      description: "Alfa's wiring is… creative. We've fixed:",
      items: [
        'Intermittent infotainment crashes',
        'Faulty window regulators',
        'Battery drain issues',
        'Module programming failures',
        'Sensor malfunctions'
      ]
    },
    {
      title: 'Oil Leak Repairs',
      icon: Wrench,
      description: 'The 2.0T and 2.9L engines love to mark their territory. We seal:',
      items: [
        'Timing cover leaks ($1,200–$1,800)',
        'MultiAir actuator leaks ($600–$900)',
        'Valve cover gaskets',
        'Oil pan gaskets',
        'Turbo oil line leaks'
      ]
    },
    {
      title: 'Performance Upgrades',
      icon: Settings,
      description: 'Unlock your Alfa\'s true potential:',
      items: [
        'ECU tuning (up to +50hp for Quadrifoglio)',
        'Novitec exhaust systems',
        'Lowering springs & coilovers',
        'Cold air intakes',
        'Performance brake upgrades'
      ]
    },
    {
      title: 'Common Repairs',
      icon: Car,
      description: 'We handle all common Alfa issues:',
      items: [
        'Check engine light diagnosis',
        'Transmission service & repair',
        'Suspension overhauls',
        'Brake system repairs',
        'Cooling system service'
      ]
    }
  ];

  const commonProblems = [
    {
      model: 'Giulia (2017-2024)',
      issues: [
        'Electrical gremlins in infotainment',
        'Oil consumption in 2.0T engines',
        'Transmission software glitches',
        'Window regulator failures',
        'Battery drain when parked'
      ]
    },
    {
      model: 'Stelvio (2018-2024)',
      issues: [
        'Transfer case issues (Q4 models)',
        'Adaptive cruise control faults',
        'Panoramic sunroof leaks',
        'Start/stop system failures',
        'HVAC blend door actuators'
      ]
    },
    {
      model: 'Quadrifoglio Models',
      issues: [
        'Limp mode under acceleration',
        'Carbon buildup in intake',
        'Coolant hose failures',
        'Differential cooling issues',
        'Torque vectoring system faults'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Alfa Romeo Repair Dallas | Italian Auto Specialists | Paradise-Performance</title>
        <meta name="description" content="Expert Alfa Romeo repair in Dallas. Specializing in Giulia, Stelvio diagnostics, oil leaks & electrical issues. European auto repair specialists at fair prices. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/alfa-romeo" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/alfa-romeo" />
        <meta property="og:title" content="Alfa Romeo Repair Dallas | Italian Auto Specialists" />
        <meta property="og:description" content="Dallas Alfa Romeo specialists. Giulia, Stelvio, Quadrifoglio experts. MES diagnostics, fair prices. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/alfa-romeo-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Alfa Romeo Repair Dallas | Italian Auto Specialists" />
        <meta name="twitter:description" content="Dallas Alfa Romeo specialists. Giulia, Stelvio, Quadrifoglio experts. MES diagnostics, fair prices." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/alfa-romeo-repair.webp" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Alfa Romeo Repair Dallas",
            "url": "https://paradise-performance.com/services/european-car-repair/alfa-romeo",
            "description": "Expert Alfa Romeo repair and maintenance in Dallas. Specializing in electrical diagnostics, oil leak repairs, and performance upgrades.",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "28"
            }
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does Alfa Romeo maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Alfa Romeo maintenance costs average $1,100 to $1,600 per year depending on the model. An oil change runs $120-$160 at an independent shop (vs $200-$300 at dealerships). Common repairs like timing cover oil leaks cost $1,200-$1,800. At Paradise-Performance near Dallas, we're typically 30-40% less than Alfa Romeo dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "Are Alfa Romeos expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Alfa Romeos are among the more expensive European cars to maintain. Annual costs average $1,100-$1,600. Common issues include electrical problems, oil leaks, and transmission software glitches. However, using an independent Italian car specialist instead of the dealership saves 30-40% on labor and parts."
                }
              },
              {
                "@type": "Question",
                "name": "What are common Alfa Romeo Giulia problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common Alfa Romeo Giulia issues include: electrical gremlins (infotainment crashes, battery drain), oil consumption in 2.0T engines, transmission software glitches, window regulator failures, and MultiAir actuator oil leaks. Quadrifoglio models may experience limp mode under hard acceleration and carbon buildup. Regular maintenance prevents most issues."
                }
              },
              {
                "@type": "Question",
                "name": "Is the Alfa Romeo Giulia reliable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Alfa Romeo Giulia has improved reliability since 2019 model year. Early 2017-2018 models had more electrical issues. The 2.0L turbo engine is generally reliable but may have oil consumption. The 2.9L twin-turbo V6 in Quadrifoglio models is robust when maintained. Regular service at 7,500-mile intervals is recommended."
                }
              },
              {
                "@type": "Question",
                "name": "How often should I service my Alfa Romeo?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Alfa Romeo recommends service every 10,000 miles or 12 months. We recommend: oil change every 7,500 miles (especially in Texas heat), brake fluid every 2 years, transmission fluid every 40,000 miles, and annual electrical system inspection. Quadrifoglio models benefit from carbon cleaning every 40,000 miles."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Alfa Romeo"
        brandColor="#C8102E"
        tagline="Keep Your Italian Beauty Running Smoothly"
        description="Alfa Romeos are passion machines—but 1 in 3 develops electrical gremlins or oil leaks by 60,000 miles. At Paradise-Performance, we speak Alfa's language and fix what most shops can't."
        features={alfaFeatures}
        heroImage="/assets/assets/images/alfa.webp"
        heroImageAlt="Alfa Romeo Service Dallas"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Introduction */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Alfa Romeo Owners Trust Us in Dallas</h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-lg text-gray-700 mb-4">
                  Alfa Romeos aren't just cars—they're temperamental Italian masterpieces. Generic shops often 
                  misdiagnose issues like electrical faults, oil leaks, or check engine lights. As <strong>Dallas' 
                  premier European auto repair</strong> specialists, we use Alfa-specific diagnostic tools 
                  (including <strong>MES</strong> software) and OEM parts to solve these headaches.
                </p>
                <p className="text-lg text-gray-700">
                  With over 15 years of experience working on Italian vehicles, our technicians understand the 
                  unique engineering philosophy and common failure points that plague these passionate machines. 
                  We've invested in the specialized equipment and training needed to properly service your Alfa Romeo.
                </p>
              </div>
              <div className="relative h-80 rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-cover-gasket.webp"
                  alt="professional Alfa Romeo technician working on a Giulia"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Services Grid */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Alfa Romeo Repair Services</h2>
            <p className="text-lg text-gray-700 mb-8">
              We service all modern Alfas (Giulia, Stelvio, 4C) and classics (Spider, GTV). Here's what we specialize in:
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div 
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg p-6"
                >
                  <div className="flex items-start mb-4">
                    <service.icon className="w-12 h-12 text-[#C8102E] mr-4 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gray-900">{service.title}</h3>
                      <p className="text-gray-700 mb-3">{service.description}</p>
                      <ul className="space-y-2">
                        {service.items.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Common Problems by Model */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Common Alfa Romeo Issues We Fix</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {commonProblems.map((model, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-[#C8102E]">
                  <h3 className="text-xl font-semibold mb-4 text-[#C8102E]">{model.model}</h3>
                  <ul className="space-y-2">
                    {model.issues.map((issue, idx) => (
                      <li key={idx} className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Comparison Table */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Why We're Dallas' Best Alfa Romeo Repair Option</h2>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#C8102E] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left">Service Factor</th>
                      <th className="px-6 py-4 text-center">Dealership</th>
                      <th className="px-6 py-4 text-center">Generic Shop</th>
                      <th className="px-6 py-4 text-center bg-gray-900">Paradise-Performance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 font-medium">Alfa-Specific Tools</td>
                      <td className="px-6 py-4 text-center">✓</td>
                      <td className="px-6 py-4 text-center text-red-600">✗</td>
                      <td className="px-6 py-4 text-center bg-green-50 font-bold">✓ (MES, SDD)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-medium">OEM Parts Access</td>
                      <td className="px-6 py-4 text-center">✓</td>
                      <td className="px-6 py-4 text-center">Limited</td>
                      <td className="px-6 py-4 text-center bg-green-50 font-bold">✓</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-medium">Wait Time</td>
                      <td className="px-6 py-4 text-center">1-2 weeks</td>
                      <td className="px-6 py-4 text-center">Varies</td>
                      <td className="px-6 py-4 text-center bg-green-50 font-bold">2-3 days</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 font-medium">Warranty</td>
                      <td className="px-6 py-4 text-center">Manufacturer</td>
                      <td className="px-6 py-4 text-center">30-90 days</td>
                      <td className="px-6 py-4 text-center bg-green-50 font-bold">12 months/12k miles</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Case Study */}
          <motion.div {...fadeInUp} className="mb-16 bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Real Fix: 2017 Giulia Quadrifoglio "Limp Mode"</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-red-600">The Problem</h3>
                <p className="text-gray-700 mb-4">
                  Customer came in with intermittent limp mode under hard acceleration. Initial diagnosis suggested a turbocharger issue.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                    <span>Limp mode at 4,000+ RPM</span>
                  </li>
                  <li className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                    <span>P0299 turbocharger underboost code</span>
                  </li>
                  <li className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                    <span>Reduced power warning on dash</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-3 text-green-600">Our Solution</h3>
                <p className="text-gray-700 mb-4">
                  Using our MES diagnostic system, we performed a smoke test and found a small split in the 
                  charge air cooler hose. A simple $380 hose replacement fixed the issue completely.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Proper diagnosis saved $3,820</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Fixed in 2 hours vs. 2 days</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Full power restored immediately</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Testimonial */}
          <motion.div {...fadeInUp} className="mb-16 bg-[#C8102E] text-white rounded-lg p-8">
            <div className="max-w-3xl mx-auto text-center">
              <Star className="w-12 h-12 mx-auto mb-4" />
              <blockquote className="text-xl italic mb-4">
                "Paradise-Performance fixed my Stelvio's electrical issues after two dealership visits failed. 
                These guys actually understand Alfas! They diagnosed the problem in an hour and had me back 
                on the road the same day. Highly recommend for any Alfa owner in Dallas."
              </blockquote>
              <cite className="text-lg font-semibold">— Sofia T., Highland Park</cite>
              <div className="flex justify-center mt-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-current" />
                ))}
              </div>
            </div>
          </motion.div>

          {/* All Models Serviced */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">All Alfa Romeo Models Serviced</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-[#C8102E] mb-3">Current Models</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Giulia (All Trims)</li>
                  <li>• Giulia Quadrifoglio</li>
                  <li>• Stelvio</li>
                  <li>• Stelvio Quadrifoglio</li>
                  <li>• Tonale (Hybrid)</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-[#C8102E] mb-3">Recent Models</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• 4C Spider</li>
                  <li>• 4C Coupe</li>
                  <li>• Giulietta</li>
                  <li>• MiTo</li>
                  <li>• 159</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-[#C8102E] mb-3">Classic Models</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Spider (All Years)</li>
                  <li>• GTV</li>
                  <li>• 164</li>
                  <li>• Milano/75</li>
                  <li>• Alfetta</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div {...fadeInUp} className="text-center bg-gray-900 text-white rounded-xl p-12">
            <img 
              src="/assets/assets/images/new/euro-repair-img/pp-logo-nbg.webp"
              alt="Alfa Romeo Logo"
              className="w-32 h-auto mx-auto mb-6"
            />
            <h2 className="text-3xl font-bold mb-4">Stop the Alfa Romeo Frustration</h2>
            <p className="text-xl mb-8 text-gray-300">
              Don't let a small issue become a big repair bill. Dallas' top Alfa specialists are ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:8173822316" 
                className="inline-block px-8 py-4 bg-[#C8102E] text-white font-bold rounded-lg hover:bg-red-700 transition-colors"
              >
                <Phone className="inline-block w-5 h-5 mr-2" />
                Call (817) 382-2316
              </a>
              <Link 
                to="/contact" 
                className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Online
              </Link>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-gray-400">
                <MapPin className="inline-block w-4 h-4 mr-1" />
                305 Co Rd 3470, Paradise, TX 76073 • Just 40 minutes from Dallas
              </p>
            </div>
          </motion.div>

          {/* Related Services */}
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Also servicing other Italian & European brands: {' '}
              <Link to="/services/european-car-repair/ferrari" className="text-[#C8102E] hover:underline">Ferrari</Link>, {' '}
              <Link to="/services/european-car-repair/maserati" className="text-[#C8102E] hover:underline">Maserati</Link>, {' '}
              <Link to="/services/european-car-repair/fiat" className="text-[#C8102E] hover:underline">Fiat</Link>, {' '}
              and all <Link to="/services/european-car-repair" className="text-[#C8102E] hover:underline">European vehicles</Link>
            </p>
          </div>
        </div>
      </div>

      {/* Alfa Romeo Vehicles For Sale */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory make="Alfa Romeo" limit={4} title="Alfa Romeo Vehicles For Sale" />
      </div>
    </>
  );
};

export default AlfaRomeoService;
